import React, { useMemo } from "react";
import { BarChart3, Trophy, Target, Activity, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, PieChart, Pie } from "recharts";
import { PageHeader } from "@/components/shell/TopBar";
import { MetricCard, IntelligenceCard } from "@/components/cards/Cards";
import { MarketBadge } from "@/components/badges/Badges";
import { LoadingCards, ErrorState, EmptyState } from "@/components/common/States";
import { useOverview, useTodayPerformance, useLive } from "@/hooks/useAtlas";
import { resolveMarket } from "@/presentation/marketRegistry";
import { lifecycleOf } from "@/presentation/resultPresentation";
import { fmtNum, fmtPct } from "@/lib/format";
import { useI18n } from "@/i18n";

export default function Performance() {
  const { d, lang } = useI18n();
  const ov = useOverview();
  const today = useTodayPerformance();
  const live = useLive(120);

  const o = ov.data;

  // market distribution from real resolved live signals
  const marketDist = useMemo(() => {
    const rows = (live.data?.rows || []).filter((r) => lifecycleOf(r.status, r.won) !== "PENDING");
    const map = new Map<string, { wins: number; total: number }>();
    rows.forEach((r) => {
      const e = map.get(r.market_code) || { wins: 0, total: 0 };
      e.total += 1; if (lifecycleOf(r.status, r.won) === "WIN") e.wins += 1;
      map.set(r.market_code, e);
    });
    return Array.from(map.entries())
      .map(([code, v]) => ({ code, label: resolveMarket(code, lang).label, ...v, rate: v.total ? (v.wins / v.total) * 100 : 0 }))
      .sort((a, b) => b.total - a.total).slice(0, 8);
  }, [live.data, lang]);

  const pie = o ? [
    { name: d.result.win, value: o.live.wins, fill: "#25d366" },
    { name: d.result.lose, value: o.live.losses, fill: "#ff3b47" },
    ...(o.live.pending > 0 ? [{ name: d.result.pending, value: o.live.pending, fill: "#e9c063" }] : []),
  ] : [];

  return (
    <div data-testid="performance-page">
      <PageHeader title={d.nav.performance}
        subtitle={lang === "tr" ? "Sonuçlanan sinyallerin performansı." : "Performance of resolved signals."} />

      {ov.isLoading ? <LoadingCards count={4} /> : ov.isError || !o ? <ErrorState error={ov.error as any} onRetry={ov.refetch} />
        : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <MetricCard index={0} label={lang === "tr" ? "Toplam Sinyal (Canlı)" : "Total Signals (LIVE)"} value={fmtNum(o.live.total)} accent="neutral" icon={<Activity className="h-4 w-4" />} />
              <MetricCard index={1} label={lang === "tr" ? "Sonuçlanan" : "Resolved"} value={fmtNum(o.live.resolved)} accent="atlas" icon={<Target className="h-4 w-4" />} />
              <MetricCard index={2} label={`${d.result.win} / ${d.result.lose}`} value={`${fmtNum(o.live.wins)} / ${fmtNum(o.live.losses)}`} accent="gold" icon={<Trophy className="h-4 w-4" />} />
              <MetricCard index={3} label={lang === "tr" ? "İsabet Oranı" : "Hit Rate"} value={fmtPct(o.live.hit_rate, 1)} accent="atlas" icon={<TrendingUp className="h-4 w-4" />} sub={lang === "tr" ? "sonuçlanan verilerden" : "from resolved data"} />
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-[1fr_1.4fr]">
              <IntelligenceCard title={lang === "tr" ? "Sonuç Dağılımı" : "Result Distribution"}>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={pie} dataKey="value" nameKey="name" innerRadius={64} outerRadius={92} paddingAngle={3} stroke="none">
                        {pie.map((e, i) => <Cell key={i} fill={e.fill} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: "#0b0f1c", border: "1px solid #1b2540", borderRadius: 12, color: "#fff" }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 flex justify-center gap-4">
                  {pie.map((e) => <span key={e.name} className="flex items-center gap-1.5 text-xs text-muted-foreground"><span className="h-2.5 w-2.5 rounded-full" style={{ background: e.fill }} /> {e.name} <b className="num text-foreground/90">{fmtNum(e.value)}</b></span>)}
                </div>
              </IntelligenceCard>

              <IntelligenceCard title={lang === "tr" ? "Market Bazlı İsabet (Sonuçlanan)" : "Hit Rate by Market (Resolved)"}>
                {live.isLoading ? <div className="skeleton h-64 rounded-xl" /> : marketDist.length === 0 ? <EmptyState title={d.states.insufficient} />
                  : (
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={marketDist} layout="vertical" margin={{ left: 10, right: 20 }}>
                          <XAxis type="number" domain={[0, 100]} tick={{ fill: "#7c8aa5", fontSize: 11 }} axisLine={false} tickLine={false} unit="%" />
                          <YAxis type="category" dataKey="label" width={120} tick={{ fill: "#9fb0c9", fontSize: 11 }} axisLine={false} tickLine={false} />
                          <Tooltip cursor={{ fill: "rgba(255,255,255,0.03)" }} contentStyle={{ background: "#0b0f1c", border: "1px solid #1b2540", borderRadius: 12, color: "#fff" }} formatter={(v: any, _n, p: any) => [`${Number(v).toFixed(0)}% · ${p.payload.total} ${lang === "tr" ? "örnek" : "samples"}`, p.payload.label]} />
                          <Bar dataKey="rate" radius={[0, 6, 6, 0]} barSize={16}>
                            {marketDist.map((e, i) => <Cell key={i} fill={e.rate >= 55 ? "#25d366" : e.rate >= 45 ? "#e9c063" : "#ff3b47"} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  )}
              </IntelligenceCard>
            </div>

            {/* today */}
            {today.data && (
              <IntelligenceCard className="mt-6" title={lang === "tr" ? "Bugün · Market Dökümü" : "Today · Market Breakdown"}>
                <div className="grid gap-2 sm:grid-cols-2">
                  {today.data.data.markets.map((m) => (
                    <div key={m.market_code} className="flex items-center justify-between gap-2 rounded-xl border border-white/5 bg-white/[0.02] p-3">
                      <MarketBadge code={m.market_code} size="sm" />
                      <span className="num text-xs text-muted-foreground"><b className="text-foreground/90">{fmtPct(m.hit_rate, 0)}</b> · {m.wins}W {m.losses}L · {m.samples}</span>
                    </div>
                  ))}
                </div>
              </IntelligenceCard>
            )}
          </>
        )}
    </div>
  );
}
